import React, { useState } from 'react';
import { ArrowUpRight, Loader2, PlayCircle, Newspaper } from 'lucide-react';
import { toast } from 'sonner';
import { MEDIA_RECOGNITION } from '../data/content';
import { api } from '../services/api';
import SEO from '../components/SEO';

const Media = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.submitInquiry({
        type: 'media',
        name: form.name,
        email: form.email,
        message: form.message,
      });
      toast.success('Media inquiry received. Our team will respond shortly.');
      setForm({ name: '', email: '', message: '' });
    } catch {
      toast.error('Unable to submit at this time. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const articles = MEDIA_RECOGNITION.filter((m) => m.type !== 'Television Interview');
  const latestArticles = articles.filter((m) => m.latest);
  const otherArticles = articles.filter((m) => !m.latest);
  const interviews = MEDIA_RECOGNITION.filter((m) => m.type === 'Television Interview');

  return (
    <div className="bg-[#050505] text-white" data-testid="media-page">
      <SEO
        title="Media & Recognition — Bronto Platforms in the Press"
        description="Press coverage and recognition of Bronto Platforms Private Limited and its founder Jeet Santosh Bhaskar across English, Marathi, and broadcast publications in India."
        path="/media"
      />
      {/* Hero */}
      <section className="relative pt-20 pb-24 px-6 lg:px-10 overflow-hidden">
        <div className="aurora-bg" />
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-6">
            Media & Recognition
          </div>
          <h1 className="text-display text-5xl md:text-7xl lg:text-8xl mb-10 max-w-5xl">
            <span className="rainbow-text">Bronto Platforms</span> in the press.
          </h1>
          <p className="text-xl md:text-2xl text-white/65 max-w-4xl leading-relaxed font-light">
            Featured coverage and recognition of <span className="text-white font-medium">Jeet Santosh Bhaskar</span>, Founder of Bronto Intelligence Private Limited, and the company&apos;s flagship platforms across leading broadcast publications in India and the world.
          </p>
        </div>
      </section>

      {/* Featured Personality */}
      <section className="py-12 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-7xl mx-auto rounded-3xl glass-strong p-8 md:p-12 relative overflow-hidden">
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full blur-3xl opacity-20 bg-gradient-to-br from-orange-500 via-pink-500 to-purple-500" />
          <div className="relative z-10 grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-3">
              <div className="aspect-square rounded-2xl overflow-hidden ring-1 ring-white/10">
                <img
                  src="https://customer-assets-jai6qajn.emergentagent.net/job_fa4320f1-9481-4dc5-871c-cc826bab00cf/artifacts/l0smod3f_Jeet%20Santosh%20Bhaskar.jpg"
                  alt="Jeet Santosh Bhaskar"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="md:col-span-9">
              <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-3">
                Featured
              </div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-3">
                Jeet Santosh Bhaskar
              </h2>
              <p className="text-base text-white/60 mb-4">
                Founder, Bronto Intelligence Private Limited
              </p>
              <p className="text-base md:text-lg text-white/75 leading-relaxed max-w-3xl">
                The press coverage below centers on the work of Jeet Santosh Bhaskar — founder of the Bronto Intelligence and architect of the BrontoAI, BrontoY, MSPConnect, and Bronto Cosmos platforms — and his contribution to India&apos;s artificial intelligence and technology ecosystem.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Press Coverage */}
      <section className="py-20 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-3">
            <Newspaper className="h-4 w-4 text-white/40" />
            <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40">
              Press Coverage
            </div>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-16 max-w-4xl">
            Verified press references.
          </h2>

          {latestArticles.length > 0 && (
            <div className="mb-16" data-testid="latest-news-section">
              <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-6">
                Latest News
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                {latestArticles.map((item) => (
                  <a
                    key={item.id}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group glass rounded-3xl p-8 md:p-10 hover-lift block"
                    data-testid={`media-coverage-${item.id}`}
                  >
                    <div className="flex items-start justify-between gap-6 mb-8">
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-white/40 mb-3">
                          {item.publication}
                        </div>
                        <h3 className="text-xl md:text-2xl font-bold leading-snug tracking-tight">
                          {item.headline}
                        </h3>
                      </div>
                      <div className="w-12 h-12 rounded-full bg-white/5 group-hover:bg-white group-hover:text-black flex items-center justify-center transition-all shrink-0">
                        <ArrowUpRight className="h-5 w-5" />
                      </div>
                    </div>

                    <p className="text-sm text-white/60 leading-relaxed mb-6">
                      {item.summary}
                    </p>

                    <div className="flex items-center gap-3 text-[11px] text-white/40 pt-4 border-t border-white/5">
                      <span>{item.language}</span>
                      <span>·</span>
                      <span>{item.date}</span>
                      <span className="ml-auto">Read article</span>
                    </div>
                  </a>
                ))}
              </div>

              <div className="flex items-center gap-4 mt-12" data-testid="latest-news-divider">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-[11px] font-medium uppercase tracking-[0.25em] text-white/40">
                  Earlier Coverage
                </span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-5">
            {otherArticles.map((item) => (
              <a
                key={item.id}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group glass rounded-3xl p-8 md:p-10 hover-lift block"
                data-testid={`media-coverage-${item.id}`}
              >
                <div className="flex items-start justify-between gap-6 mb-8">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.2em] text-white/40 mb-3">
                      {item.publication}
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold leading-snug tracking-tight">
                      {item.headline}
                    </h3>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-white/5 group-hover:bg-white group-hover:text-black flex items-center justify-center transition-all shrink-0">
                    <ArrowUpRight className="h-5 w-5" />
                  </div>
                </div>

                <p className="text-sm text-white/60 leading-relaxed mb-6">
                  {item.summary}
                </p>

                <div className="flex items-center gap-3 text-[11px] text-white/40 pt-4 border-t border-white/5">
                  <span>{item.language}</span>
                  <span>·</span>
                  <span>{item.date}</span>
                  <span className="ml-auto">Read article</span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Television Interviews */}
      {interviews.length > 0 && (
        <section className="py-20 px-6 lg:px-10 border-t border-white/5">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-3">
              <PlayCircle className="h-4 w-4 text-white/40" />
              <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40">
                Television
              </div>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-16 max-w-4xl">
              Broadcast appearances.
            </h2>

            <div className="grid md:grid-cols-2 gap-5">
              {interviews.map((item) => (
                <a
                  key={item.id}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group glass rounded-3xl p-8 md:p-10 hover-lift block relative overflow-hidden"
                  data-testid={`media-coverage-${item.id}`}
                >
                  <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full blur-3xl opacity-20 bg-gradient-to-br from-red-500 to-pink-500" />
                  <div className="relative z-10">
                    <div className="flex items-start justify-between gap-6 mb-8">
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-white/40 mb-3">
                          {item.publication} · Interview
                        </div>
                        <h3 className="text-xl md:text-2xl font-bold leading-snug tracking-tight">
                          {item.headline}
                        </h3>
                      </div>
                      <div className="w-12 h-12 rounded-full bg-white/5 group-hover:bg-white group-hover:text-black flex items-center justify-center transition-all shrink-0">
                        <PlayCircle className="h-5 w-5" />
                      </div>
                    </div>

                    <p className="text-sm text-white/60 leading-relaxed mb-6">
                      {item.summary}
                    </p>

                    <div className="flex items-center gap-3 text-[11px] text-white/40 pt-4 border-t border-white/5">
                      <span>{item.language}</span>
                      <span>·</span>
                      <span>{item.date}</span>
                      <span className="ml-auto">Watch interview</span>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Press Inquiry Form */}
      <section className="py-24 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-3">
            Press Inquiries
          </div>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
            For media and press.
          </h2>
          <p className="text-base md:text-lg text-white/60 leading-relaxed mb-3 max-w-2xl">
            Journalists, editors, and media representatives seeking interviews, statements, or further information about Bronto Platforms are encouraged to reach out via the form below.
          </p>
          <p className="text-sm text-white/45 mb-12">
            Inquiries are routed to <span className="text-white/70 font-medium">media@brontointelligence.com</span>.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="press-inquiry-form">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.2em] text-white/40 mb-3">
                  Name / Publication
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-4 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
                  placeholder="Your name or publication"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.2em] text-white/40 mb-3">
                  Email
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-4 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
                  placeholder="you@publication.com"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.2em] text-white/40 mb-3">
                Inquiry
              </label>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                required
                rows={6}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-4 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors resize-none"
                placeholder="Brief description of your inquiry..."
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="press-inquiry-submit"
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>Submit Inquiry</>
              )}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Media;
